import React, { useState, useEffect } from 'react';
import { motion } from "framer-motion";
import {
  Upload,
  Search,
  CheckCircle,
  ArrowRight,
  UserPlus,
} from "lucide-react";
import axios from 'axios';
import JobCard from './JobCard';
import { Box } from '@mui/material';

const LandingPage = () => {
  const [jobs, setJobs] = useState([]);

  const fetchJobs = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/job/`, {
        params: { limit: 8 },
        headers: {
          'X-API-Key': process.env.REACT_APP_PUBLIC_API_KEY
        }
      });
      const results = response.data.results;
      setJobs(results);
    } catch (error) {
      // Silently fail for landing page
      console.error('Failed to fetch jobs:', error);
    }
  };

  useEffect(() => {
    fetchJobs();
  }, []);

  // All CTA buttons redirect to work URL
  const handleSignIn = () => {
    window.location.href = `${process.env.REACT_APP_WORK_URL}/auth/sign-in`;
  };

  const handleSignUp = () => {
    window.location.href = `${process.env.REACT_APP_WORK_URL}/auth/sign-up`;
  };

  const handleViewAllOpportunities = () => {
    window.location.href = `${process.env.REACT_APP_WORK_URL}/opportunities`;
  };

  const handleJobCardClick = (jobId) => {
    window.location.href = `${process.env.REACT_APP_WORK_URL}/opportunities/${jobId}/details`;
  };
  
  const fadeInUp = {
    initial: { opacity: 0, y: 60 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 },
  };

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  // const features = [
  //   {
  //     icon: <Brain className="w-8 h-8" />,
  //     title: "AI-Powered Matching",
  //     description:
  //       "Our advanced AI analyzes resumes and job requirements to find perfect matches in seconds.",
  //   },
  //   {
  //     icon: <Zap className="w-8 h-8" />,
  //     title: "Lightning Fast",
  //     description:
  //       "Reduce hiring time from weeks to days with automated screening and ranking.",
  //   },
  //   {
  //     icon: <Target className="w-8 h-8" />,
  //     title: "Perfect Fit",
  //     description:
  //       "Go beyond keywords to match skills, experience, and cultural alignment.",
  //   },
  //   {
  //     icon: <Users className="w-8 h-8" />,
  //     title: "For Everyone",
  //     description:
  //       "Whether you're hiring or job hunting, we connect the right people with the right opportunities.",
  //   },
  // ];

  const steps = [
    {
      icon: <Upload className="w-12 h-12" />,
      title: "Build Your Profile",
      description: "Upload your resume and complete your profile",
    },
    {
      icon: <Search className="w-12 h-12" />,
      title: "AI Analysis",
      description: "Our AI analyzes and ranks your profile",
    },
    {
      icon: <CheckCircle className="w-12 h-12" />,
      title: "Connect and Apply",
      description: "Connect with perfect matches and start applying",
    },
  ];

  return (
    <div className="min-h-screen bg-white">

      {/* Hero Section */}
      <section className="bg-gray-10 text-gray-800">
        <div className="container mx-auto px-4 py-20 md:py-32">
          <div className="text-center max-w-6xl mx-auto">
            <motion.div
              initial="initial"
              animate="animate"
              variants={staggerContainer}
            >
              <motion.h1
                variants={fadeInUp}
                className="text-2xl md:text-4xl mb-6 leading-tight text-gray-800"
              >
                <span className="whitespace-nowrap">You Have Talent?</span><br />
                <span className="text-blue-500 font-black">We can place you in the global market.</span>
              </motion.h1>

              <motion.p
                variants={fadeInUp}
                className="text-xl md:text-xl mb-8 text-gray-800 text-pretty leading-relaxed max-w-xl mx-auto"
              >
                We can instantly evaluate your resume and find the right opportunities for you around the world.
              </motion.p>

              <motion.div
                variants={fadeInUp}
                className="mb-12"
              >
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <button
                    onClick={handleSignIn}
                    className="inline-flex items-center px-8 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-lg font-semibold"
                  >
                    <UserPlus className="w-5 h-5 mr-2" />
                    Upload Your Resume Now
                  </button>
                </motion.div>
              </motion.div>

              {/* Stats Section */}
              <motion.div
                variants={fadeInUp}
                className="grid grid-cols-1 sm:grid-cols-2 gap-8 max-w-md mx-auto"
              >
                <div className="text-center">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 0.6, delay: 0.8 }}
                    className="text-4xl md:text-5xl font-black text-gray-800 mb-2"
                  >
                    1,250+
                  </motion.div>
                  <p className="text-gray-800 text-sm font-medium">
                    Opportunities Created
                  </p>
                </div>
                <div className="text-center">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 0.6, delay: 1.0 }}
                    className="text-4xl md:text-5xl font-black text-gray-800 mb-2"
                  >
                    850+
                  </motion.div>
                  <p className="text-gray-800 text-sm font-medium">
                    Talents Matched
                  </p>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>


      {/* Latest Opportunities Section */}
      <section className="py-20 bg-gray-200">
        <div className="container mx-auto px-4">
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-16"
          >
            <motion.h2
              variants={fadeInUp}
              className="text-2xl md:text-4xl font-black mb-4 text-gray-800 text-balance"
            >
              Latest Opportunities
            </motion.h2>
            <motion.p
              variants={fadeInUp}
              className="text-xl text-gray-800 max-w-2xl mx-auto text-pretty"
            >
              Discover the newest job openings waiting for the right talent
            </motion.p>
          </motion.div>

          {jobs.length > 0 ? (
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(2, 1fr)',
                    md: 'repeat(3, 1fr)',
                    lg: 'repeat(4, 1fr)'
                  },
                  gap: 3,
                  width: '100%'
                }}
              >
                {jobs.filter(Boolean).map((job, idx) => (
                  <Box key={idx} sx={{ width: '100%', minWidth: 0 }}>
                    <JobCard job={job} onApplyClick={handleJobCardClick} />
                  </Box>
                ))}
              </Box>
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="text-center py-12"
            >
              <p className="text-gray-600 text-lg">Loading latest opportunities...</p>
            </motion.div>
          )}

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="text-center mt-12"
          >
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <button
                onClick={handleViewAllOpportunities}
                className="inline-flex items-center px-8 py-3 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors text-lg font-semibold"
              >
                View All Opportunities
                <ArrowRight className="w-5 h-5 ml-2" />
              </button>
            </motion.div>
          </motion.div>
        </div>
      </section>


      {/* Features Section */}
      <section className="py-20 bg-gray-10">
        <div className="container mx-auto px-4">
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-16"
          >
            <motion.h2
              variants={fadeInUp}
              className="text-3xl md:text-5xl font-black mb-4 text-gray-800 text-balance"
            >
              Why Choose Our Platform?
            </motion.h2>
            <motion.p
              variants={fadeInUp}
              className="text-xl text-gray-800 max-w-2xl mx-auto text-pretty"
            >
              Experience the next generation of job searching and talent discovery
            </motion.p>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            className="grid md:grid-cols-3 gap-8"
          >
            <motion.div variants={fadeInUp} className="text-center p-6 bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-8 h-8 text-gray-800" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-3 text-gray-800">AI-Powered Matching</h3>
              <p className="text-gray-800">
                Our advanced AI algorithms match the right candidates with the right opportunities for perfect fits.
              </p>
            </motion.div>

            <motion.div variants={fadeInUp} className="text-center p-6 bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-8 h-8 text-gray-800" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-3 text-gray-800">Verified Opportunities</h3>
              <p className="text-gray-800">
                All job postings are verified and vetted to ensure authentic and legitimate opportunities.
              </p>
            </motion.div>

            <motion.div variants={fadeInUp} className="text-center p-6 bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-8 h-8 text-gray-800" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-3 text-gray-800">Seamless Experience</h3>
              <p className="text-gray-800">
                Intuitive interface and smooth workflow make job searching and hiring effortless.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* For Talents Section */}
      <section className="py-20 bg-gray-10">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="flex justify-center order-2 lg:order-1"
            >
               <img
                 src="/undraw_career-progress_vfq5.svg"
                 alt="Career Progress"
                 className="w-full max-w-md drop-shadow-lg"
               />
            </motion.div>

            <motion.div
              initial="initial"
              whileInView="animate"
              viewport={{ once: true }}
              variants={staggerContainer}
              className="order-1 lg:order-2"
            >
              <motion.div variants={fadeInUp} className="mb-4">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800">
                  <UserPlus className="w-4 h-4 mr-2" />
                  For Talents
                </span>
              </motion.div>

              <motion.h2
                variants={fadeInUp}
                className="text-3xl md:text-5xl font-black mb-6 text-gray-800 text-balance"
              >
                Get discovered for who you really are.
              </motion.h2>

              <motion.div variants={fadeInUp} className="space-y-4 mb-8">
                {[
                  "Upload your resume once",
                  "Let AI highlight your strengths",
                  "Get matched with perfect opportunities",
                ].map((step, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.2 }}
                    className="flex items-center gap-3"
                  >
                    <CheckCircle className="w-6 h-6 text-gray-800 flex-shrink-0" />
                    <span className="text-lg text-gray-800">{step}</span>
                  </motion.div>
                ))}
              </motion.div>

              <motion.div
                variants={fadeInUp}
                className="p-6 bg-gray-700 text-white rounded-lg"
              >
                <p className="text-lg font-semibold">
                  Result: More opportunities, less rejection.
                </p>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 bg-gray-10">
        <div className="container mx-auto px-4">
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-16"
          >
            <motion.h2
              variants={fadeInUp}
              className="text-3xl md:text-5xl font-black mb-4 text-gray-800 text-balance"
            >
              How It Works
            </motion.h2>
            <motion.p
              variants={fadeInUp}
              className="text-xl text-gray-800 max-w-2xl mx-auto text-pretty"
            >
              Three simple steps to connect talent with opportunity
            </motion.p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {steps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                className="relative"
              >
                <div className="text-center p-8 bg-white rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
                  <motion.div
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    className="inline-flex items-center justify-center w-20 h-20 bg-gray-100 text-gray-800 rounded-full mb-6"
                  >
                    {step.icon}
                  </motion.div>
                  <h3 className="text-xl font-bold mb-3 text-gray-800">
                    {step.title}
                  </h3>
                  <p className="text-gray-800 leading-relaxed">
                    {step.description}
                  </p>
                </div>

                {index < steps.length - 1 && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.5 + index * 0.2 }}
                    className="hidden md:block absolute -right-4 top-1/2 transform -translate-y-1/2 z-10"
                  >
                    <ArrowRight className="w-8 h-8 text-gray-800" />
                  </motion.div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

       {/* CTA Section */}
       <section className="py-20 relative overflow-hidden bg-gray-300">
        <div className="container mx-auto px-4 text-center relative z-10">
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.h2
              variants={fadeInUp}
              className="text-3xl md:text-5xl font-black mb-6 text-balance text-gray-800"
            >
              The right talents. The right opportunities.{" "}
              {/* <span className="text-gray-800">The right time.</span> */}
            </motion.h2>

            <motion.p
              variants={fadeInUp}
              className="text-xl mb-8 text-gray-800 max-w-2xl mx-auto text-pretty"
            >
              Talent Match is where opportunities and potential come together.
            </motion.p>

            <motion.div variants={fadeInUp}>
              <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <button
                    onClick={handleSignUp}
                    className="inline-flex items-center px-8 py-4 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors text-lg font-semibold"
                  >
                    <UserPlus className="w-5 h-5 mr-2" />
                    Get Started for Free
                  </button>
                </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-10 text-gray-800 border-t border-gray-200">
        <div className="container mx-auto px-4 py-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-8">
            {/* Logo and Social Media */}
            <div className="lg:col-span-2">
              <div className="mb-6 px-1">
                {/* <img
                  src="/TalentMatch-logo-transparent.png"
                  alt="Talent Match"
                  className="h-10 w-auto mb-4"
                /> */}
                {/* <p className="text-gray-600 text-sm leading-relaxed">
                  Connecting talent with opportunity through AI-powered matching.
                </p> */}
                <h3 className="text-lg font-semibold mb-4 text-gray-900">San Francisco, CA</h3>
              </div>
              
              {/* Social Media Icons */}
              <div className="flex space-x-4">
                <button
                  type="button"
                  onClick={() => {/* Add LinkedIn link here */}}
                  className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center hover:bg-cyan-600 hover:text-white transition-colors text-gray-600"
                  aria-label="LinkedIn"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.338 16.338H13.67V12.16c0-.995-.017-2.277-1.387-2.277-1.39 0-1.601 1.086-1.601 2.207v4.248H8.014v-8.59h2.559v1.174h.037c.356-.675 1.227-1.387 2.526-1.387 2.703 0 3.203 1.778 3.203 4.092v4.711zM5.005 6.575a1.548 1.548 0 11-.003-3.096 1.548 1.548 0 01.003 3.096zm-1.337 9.763H6.34v-8.59H3.667v8.59zM17.668 1H2.328C1.595 1 1 1.581 1 2.298v15.403C1 18.418 1.595 19 2.328 19h15.34c.734 0 1.332-.582 1.332-1.299V2.298C19 1.581 18.402 1 17.668 1z" clipRule="evenodd" />
                  </svg>
                </button>
                <button
                  type="button"
                  onClick={() => {/* Add Twitter link here */}}
                  className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center hover:bg-cyan-600 hover:text-white transition-colors text-gray-600"
                  aria-label="Twitter"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M6.29 18.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0020 3.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.073 4.073 0 01.8 7.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 010 16.407a11.616 11.616 0 006.29 1.84" />
                  </svg>
                </button>
                <button
                  type="button"
                  onClick={() => {/* Add Facebook link here */}}
                  className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center hover:bg-cyan-600 hover:text-white transition-colors text-gray-600"
                  aria-label="Facebook"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M20 10C20 4.477 15.523 0 10 0S0 4.477 0 10c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V10h2.54V7.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V10h2.773l-.443 2.89h-2.33v6.988C16.343 19.128 20 14.991 20 10z" clipRule="evenodd" />
                  </svg>
                </button>
                <button
                  type="button"
                  onClick={() => {/* Add Instagram link here */}}
                  className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center hover:bg-cyan-600 hover:text-white transition-colors text-gray-600"
                  aria-label="Instagram"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 0C4.477 0 0 4.484 0 10.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0110 4.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.203 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.942.359.31.678.921.678 1.856 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0020 10.017C20 4.484 15.522 0 10 0z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
            </div>

            {/* For Companies */}
            <div>
              <h3 className="text-lg font-semibold mb-4 text-gray-900">For Companies</h3>
              <ul className="space-y-2">
                <li>
                  <a href="mailto:contact@talentmatch.ing" className="text-gray-600 hover:text-cyan-600 transition-colors text-sm">
                    Get in Touch
                  </a>
                </li>
              </ul>
            </div>

            {/* For Talents */}
            <div>
              <h3 className="text-lg font-semibold mb-4 text-gray-900">For Talents</h3>
              <ul className="space-y-2">
                <li>
                  <a href={`${process.env.REACT_APP_WORK_URL}/opportunities`} className="text-gray-600 hover:text-cyan-600 transition-colors text-sm">
                    Apply
                  </a>
                </li>
                <li>
                  <a href={`${process.env.REACT_APP_WORK_URL}/opportunities`} className="text-gray-600 hover:text-cyan-600 transition-colors text-sm">
                    Opportunities
                  </a>
                </li>
                <li>
                  <a href={`${process.env.REACT_APP_WORK_URL}/payment`} className="text-gray-600 hover:text-cyan-600 transition-colors text-sm">
                    Payment
                  </a>
                </li>
              </ul>
            </div>

            {/* Resources */}
            <div>
              <h3 className="text-lg font-semibold mb-4 text-gray-900">Resources</h3>
              <ul className="space-y-2">
                <li>
                  <a href="/careers" className="text-gray-600 hover:text-cyan-600 transition-colors text-sm">
                    Careers
                  </a>
                </li>
                <li>
                  <a href="/blog" className="text-gray-600 hover:text-cyan-600 transition-colors text-sm">
                    Blog
                  </a>
                </li>
              </ul>
            </div>

            {/* Support */}
            <div>
              <h3 className="text-lg font-semibold mb-4 text-gray-900">Support</h3>
              <ul className="space-y-2">
                <li>
                  <a href="mailto:support@talentmatch.ing" className="text-gray-600 hover:text-cyan-600 transition-colors text-sm">
                    support@talentmatch.ing
                  </a>
                </li>
              </ul>
            </div>
          </div>

          {/* Copyright */}
          <div className="border-t border-gray-200 mt-8 pt-8 text-center">
            <p className="text-gray-600 text-sm">
              © 2025 Talent Match. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
